import React from 'react';
import { Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Excel Export Button Component
export default function ExportExcel({ data, weekInfo, reportingRole, loginRole, disabled = false }) {
    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    const getPLDetails = (record) => {
        let bookedPL;
        let brokerage;
        let grossPL;

        if (reportingRole === 'admin') {
            bookedPL = record?.totalBookedProfitLoss?.admin || 0;
            brokerage = record?.totalBrokerage?.admin || 0;
            grossPL = record?.totalGrossProfitLoss?.admin || 0;
        } else if (reportingRole === 'broker') {
            bookedPL = record?.totalBookedProfitLoss?.broker || 0;
            brokerage = record?.totalBrokerage?.broker || 0;
            grossPL = record?.totalGrossProfitLoss?.broker || 0;
        } else {
            bookedPL = record?.totalBookedProfitLoss?.client || 0;
            brokerage = record?.totalBrokerage?.client || 0;
            grossPL = record?.totalGrossProfitLoss?.client || 0;
        }
        return { bookedPL, brokerage, grossPL };
    };

    const handleExportExcel = () => {
        try {
            // Prepare data for Excel
            const shouldShowBrokerColumn = loginRole !== 'broker' && reportingRole === 'client';
            const shouldShowAdminColumn = loginRole === 'superAdmin' && reportingRole === 'client';

            // Create headers based on login role and reporting role
            const headers = ['USERNAME', 'P&L', 'BROKERAGE', 'FINAL P&L'];

            if (shouldShowBrokerColumn) {
                headers.push('BROKER');
            }
            if (shouldShowAdminColumn) {
                headers.push('ADMIN');
            }

            // Prepare worksheet data
            const worksheetData = [];

            // Add title and metadata
            worksheetData.push(['P/L SUMMARY REPORT']);
            worksheetData.push([`${reportingRole.toUpperCase()}S P/L DATA`]);
            worksheetData.push([`Settlement Week: ${weekInfo}`]);
            worksheetData.push([`Generated on: ${new Date().toLocaleString('en-IN')}`]);
            worksheetData.push([]); // Empty row

            // Add headers
            worksheetData.push(headers);

            // Add data rows
            data.forEach((record) => {
                const { bookedPL, brokerage, grossPL } = getPLDetails(record);
                const userRefName = (() => {
                    if (record?.creator?.firstname !== '' && record?.creator?.lastname !== '') {
                        return `(${record.creator.firstname} ${record.creator.lastname})`;
                    }
                    if (record?.creator?.lastname !== '') {
                        return `(${record.creator.lastname})`;
                    }
                    if (record?.creator?.firstname !== '') {
                        return `(${record.creator.firstname})`;
                    }
                    return '';
                })();

                const row = [
                    `${record?.creator?.username?.toUpperCase()} ${userRefName}` || 'N/A',
                    bookedPL, // Keep as number for Excel formatting
                    brokerage,
                    grossPL,
                ]; if (shouldShowBrokerColumn) {
                    const brokerRefName = (() => {
                        if (record?.broker?.firstname !== '' && record?.broker?.lastname !== '') {
                            return `(${record?.broker?.firstname} ${record.broker.lastname})`;
                        }
                        if (record?.broker?.lastname !== '') {
                            return `(${record?.broker?.lastname})`;
                        }
                        if (record?.broker?.firstname !== '') {
                            return `(${record?.broker?.firstname})`;
                        }
                        return '';
                    })();
                    row.push(`${record?.broker?.username?.toUpperCase()} ${brokerRefName}` || 'N/A');
                }

                if (shouldShowAdminColumn) {
                    const adminRefName = (() => {
                        if (record?.admin?.firstname !== '' && record?.admin?.lastname !== '') {
                            return `(${record?.admin?.firstname} ${record.admin.lastname})`;
                        }
                        if (record?.admin?.lastname !== '') {
                            return `(${record?.admin?.lastname})`;
                        }
                        if (record?.admin?.firstname !== '') {
                            return `(${record?.admin?.firstname})`;
                        }
                        return '';
                    })();
                    row.push(`${record?.admin?.username?.toUpperCase()} ${adminRefName}` || 'N/A');
                }

                worksheetData.push(row);
            });

            // Add total row if data exists
            if (data.length > 0) {
                const totals = data.reduce(
                    (acc, record) => {
                        const { bookedPL, brokerage, grossPL } = getPLDetails(record);
                        acc.bookedPL += bookedPL;
                        acc.brokerage += brokerage;
                        acc.grossPL += grossPL;
                        return acc;
                    },
                    { bookedPL: 0, brokerage: 0, grossPL: 0 }
                );

                const totalRow = ['TOTAL', totals.bookedPL, totals.brokerage, totals.grossPL];

                if (shouldShowBrokerColumn) {
                    totalRow.push('-');
                }
                if (shouldShowAdminColumn) {
                    totalRow.push('-');
                }

                worksheetData.push(totalRow);
            }

            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            // Set column widths
            const colWidths = [
                { wch: 25 }, // USERNAME
                { wch: 15 }, // P&L
                { wch: 15 }, // BROKERAGE
                { wch: 15 }, // FINAL P&L
            ];

            if (shouldShowBrokerColumn) {
                colWidths.push({ wch: 25 }); // BROKER
            }
            if (shouldShowAdminColumn) {
                colWidths.push({ wch: 25 }); // ADMIN
            }

            worksheet['!cols'] = colWidths;

            // Style the header row (row 6, 0-indexed as row 5)
            const headerRowIndex = 5;
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            // Apply styles to header row
            for (let col = range.s.c; col <= range.e.c; col += 1) {
                const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });
                if (!worksheet[cellAddress]) {
                    // Skip if cell doesn't exist
                } else {
                    worksheet[cellAddress].s = {
                        font: { bold: true, color: { rgb: 'FFFFFF' } },
                        fill: { fgColor: { rgb: '2196F3' } },
                        alignment: { horizontal: 'center', vertical: 'center' },
                        border: {
                            top: { style: 'thin', color: { rgb: '000000' } },
                            bottom: { style: 'thin', color: { rgb: '000000' } },
                            left: { style: 'thin', color: { rgb: '000000' } },
                            right: { style: 'thin', color: { rgb: '000000' } },
                        },
                    };
                }
            }

            // Apply currency formatting to P&L columns (columns 1, 2, 3)
            const dataStartRow = headerRowIndex + 1;
            const dataEndRow = headerRowIndex + data.length + 1; // +1 for total row
            for (let row = dataStartRow; row <= dataEndRow; row += 1) {
                // P&L column
                const plCell = XLSX.utils.encode_cell({ r: row, c: 1 });
                if (worksheet[plCell] && typeof worksheet[plCell].v === 'number') {
                    worksheet[plCell].z = '#,##0.00';
                    const plValue = worksheet[plCell].v;
                    let fontColor = '2196F3'; // default blue
                    if (plValue > 0) {
                        fontColor = '4CAF50'; // green
                    } else if (plValue < 0) {
                        fontColor = 'F44336'; // red
                    }
                    worksheet[plCell].s = {
                        font: { color: { rgb: fontColor } },
                        numFmt: '#,##0.00'
                    };
                }

                // Brokerage column
                const brokerageCell = XLSX.utils.encode_cell({ r: row, c: 2 });
                if (worksheet[brokerageCell] && typeof worksheet[brokerageCell].v === 'number') {
                    worksheet[brokerageCell].z = '#,##0.00';
                    worksheet[brokerageCell].s = {
                        font: { color: { rgb: '2196F3' } },
                        numFmt: '#,##0.00'
                    };
                }

                // Final P&L column
                const finalPlCell = XLSX.utils.encode_cell({ r: row, c: 3 });
                if (worksheet[finalPlCell] && typeof worksheet[finalPlCell].v === 'number') {
                    worksheet[finalPlCell].z = '#,##0.00';
                    const finalPlValue = worksheet[finalPlCell].v;
                    let fontColor = '2196F3'; // default blue
                    if (finalPlValue > 0) {
                        fontColor = '4CAF50'; // green
                    } else if (finalPlValue < 0) {
                        fontColor = 'F44336'; // red
                    }
                    worksheet[finalPlCell].s = {
                        font: { color: { rgb: fontColor } },
                        numFmt: '#,##0.00'
                    };
                }
            }      // Style the total row
            if (data.length > 0) {
                const totalRowIndex = dataEndRow;
                for (let col = range.s.c; col <= range.e.c; col += 1) {
                    const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
                    if (!worksheet[cellAddress]) {
                        // Skip if cell doesn't exist
                    } else {
                        worksheet[cellAddress].s = {
                            ...worksheet[cellAddress].s,
                            font: { bold: true, ...(worksheet[cellAddress].s?.font || {}) },
                            fill: { fgColor: { rgb: 'E0E0E0' } },
                            border: {
                                top: { style: 'thick', color: { rgb: '000000' } },
                                bottom: { style: 'thin', color: { rgb: '000000' } },
                                left: { style: 'thin', color: { rgb: '000000' } },
                                right: { style: 'thin', color: { rgb: '000000' } },
                            },
                        };
                    }
                }
            }      // Style title rows
            for (let i = 0; i < 4; i += 1) {
                const titleCell = XLSX.utils.encode_cell({ r: i, c: 0 });
                if (worksheet[titleCell]) {
                    worksheet[titleCell].s = {
                        font: { bold: true, size: i === 0 ? 16 : 12 },
                        alignment: { horizontal: 'center' },
                    };
                }
            }

            // Add worksheet to workbook
            const sheetName = `PL_Summary_${reportingRole}`;
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

            // Generate file and save
            const fileName = `PL_Summary_${reportingRole}_${weekInfo.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            saveAs(blob, fileName);
        } catch (error) {
            console.error('Error generating Excel:', error);
            alert('Error generating Excel file. Please try again.');
        }
    };

    return (
        <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={disabled || !data || data.length === 0}
            sx={{
                minWidth: 140,
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                    backgroundColor: '#2e7d32',
                },
            }}
        >
            Export Excel
        </Button>
    );
}
